/**
 * EID (Equipment Identification) Validation Service
 * Validates force plate EIDs
 */

import {
  EIDDatabase,
  EIDValidationResult,
  IEIDValidationService,
} from '../types/eid-types';

/**
 * EID Validation Service Implementation
 */
export class EIDValidationService implements IEIDValidationService {
  // EID format: 3-4 digit numeric (000-2999)
  private readonly EID_PATTERN = /^(0?[0-9]{3}|[12][0-9]{3})$/;

  constructor(private eidDatabase: EIDDatabase = {}) {}

  /**
   * Validate EID format using regex pattern
   */
  validateFormat(eid: string): boolean {
    if (!eid || typeof eid !== 'string') {
      return false;
    }

    return this.EID_PATTERN.test(eid.trim());
  }

  /**
   * Validate EID checksum (no-op, checksum validation not required)
   */
  validateChecksum(eid: string): boolean {
    return this.validateFormat(eid);
  }

  /**
   * Look up EID in database
   * Stubbed out for now
   * @param eid - EID to look up
   * @returns EID validation result
   */
  async lookup(eid: string): Promise<EIDValidationResult> {
    const normalizedEid = eid.trim().toUpperCase();

    // Check if exists in database
    const entry = this.eidDatabase[normalizedEid];

    if (entry) {
      return {
        valid: true,
        formatValid: true,
        checksumValid: true,
        existsInDatabase: true,
        deviceInfo: {
          eid: entry.eid,
          model: entry.model,
          serialNumber: entry.serialNumber,
          hardwareRevision: entry.hardwareRevision,
          manufacturingDate: entry.manufacturingDate,
          notes: entry.notes,
        },
      };
    }

    return {
      valid: false,
      formatValid: this.validateFormat(eid),
      checksumValid: this.validateChecksum(eid),
      existsInDatabase: false,
      error: 'EID not found in database',
    };
  }

  /**
   * Full validation (format only - no database lookup required)
   */
  async validate(eid: string): Promise<EIDValidationResult> {
    const normalizedEid = eid.trim();

    // Validate format using regex pattern
    const formatValid = this.validateFormat(normalizedEid);
    if (!formatValid) {
      return {
        valid: false,
        formatValid: false,
        checksumValid: false,
        existsInDatabase: false,
        error: 'Invalid EID format. Please enter a valid Equipment ID.',
      };
    }

    // Format is valid - that's all we need
    return {
      valid: true,
      formatValid: true,
      checksumValid: true,
      existsInDatabase: false,
    };
  }

  /**
   * Check if device is a G5 model
   */
  async isG5Device(eid: string): Promise<boolean> {
    const normalizedEid = eid.trim().toUpperCase();

    // Check database for model info
    const entry = this.eidDatabase[normalizedEid];
    if (entry) {
      return entry.model.toUpperCase() === 'G5';
    }

    // If not in database, assume valid (will be caught by lookup validation)
    return true;
  }

  /**
   * Load EID database from external source
   */
  async loadDatabase(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load EID database: ${response.statusText}`);
      }

      const data = await response.json();
      this.eidDatabase = data;
    } catch (error) {
      throw new Error(`Failed to load EID database: ${(error as Error).message}`);
    }
  }

  /**
   * Set database programmatically (for testing)
   */
  setDatabase(database: EIDDatabase): void {
    this.eidDatabase = database;
  }

  /**
   * Get database (for testing)
   */
  getDatabase(): EIDDatabase {
    return this.eidDatabase;
  }

}
